'use client'

import { useQuery } from '@apollo/client'
import { Splash } from 'app/_components'
import { createContext, useContext, useEffect, useState } from 'react'
import {
  GetCurrentUserWithEmailDocument,
  GetCurrentUserWithEmailQuery,
} from '~/__generated__/graphql'
import { setAnonymousUserId } from '~/libs/anonymousUserId'
import * as GA from '~/libs/ga'
import { useAccessToken } from './useAccessToken'

// ユーザーのアカウントの状態を表す.
// 以下の状態に基づいて表示画面や呼び出すAPIを出し分ける.
//
// notSignedIn: 非ログイン状態
// signedIn: 有効なアカウントでログイン中
// noProfile: CognitoにはログインしているがSamansaのアカウントが未作成
// conflict: ログイン中だが同メアドの他ログイン方法でログインしないとダメ
// preventNewSocialSignedIn: ログイン方法でSNSを使い新規登録したユーザ (SNSログイン制御前からSNSログインを使っていた人は対象外)
export type LoginState =
  | 'notSignedIn'
  | 'signedIn'
  | 'noProfile'
  | 'conflict'
  | 'preventNewSocialSignedIn'

type Provider = 'Cognito' | 'Google' | 'SignInWithApple' | 'Twitter'

type LoginInfo = {
  status: LoginState
  provider?: Provider
  email?: string
}

const LoginInfoContext = createContext<LoginInfo>({
  status: 'notSignedIn',
  provider: undefined,
  email: undefined,
})

export const LoginInfoProvider = ({ children }: { children: React.ReactNode }) => {
  const { token: accessToken, initialized: initializedAccessToken } = useAccessToken()
  const [loginInfo, setLoginInfo] = useState<LoginInfo>({
    status: 'notSignedIn',
  })
  const [initialized, setInitialized] = useState(false)
  const [splashMinElapsed, setSplashMinElapsed] = useState(false)
  const [splashVisible, setSplashVisible] = useState(true)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSplashMinElapsed(true)
    }, 400)

    return () => clearTimeout(timer)
  }, [])
  useEffect(() => {
    if (initialized && splashMinElapsed) {
      setTimeout(() => {
        setSplashVisible(false)
      }, 200)
    }
  }, [initialized, splashMinElapsed])

  const {
    data: userData,
    error: userError,
    loading,
  } = useQuery<GetCurrentUserWithEmailQuery>(GetCurrentUserWithEmailDocument)

  // ユーザーのログイン状態を更新する
  useEffect(() => {
    if (loading) return
    if (!initializedAccessToken) return

    if (userError) {
      const graphQLError = userError.graphQLErrors[0]
      const extensions = graphQLError.extensions ?? {}
      const reason = extensions.reason
      switch (reason) {
        case 'REQUIRE_PROFILE':
          setLoginInfo({ status: 'noProfile', email: userData?.currentUser?.email || '' })
          setInitialized(true)
          return
        case 'PROVIDER_MISMATCH':
          setLoginInfo({
            status: 'conflict',
            email: extensions.email as string,
            provider: extensions.provider as Provider,
          })
          setInitialized(true)
          return
        case 'NEW_SOCIAL_SIGN_UP':
          setLoginInfo({ status: 'preventNewSocialSignedIn' })
          setInitialized(true)
          return
        default:
          setLoginInfo({ status: 'notSignedIn' })
          setInitialized(true)
          GA.setUserType('notLogin')
          break
      }
    } else if (userData?.currentUser != undefined) {
      setLoginInfo({ status: 'signedIn', email: userData.currentUser.email || '' })
      if (userData.currentUser.anonymousUserId) {
        setAnonymousUserId(userData.currentUser.anonymousUserId)
      }
    } else if (accessToken === undefined) {
      setLoginInfo({ status: 'notSignedIn' })
      GA.setUserType('notLogin')
    }

    setInitialized(true)
  }, [loading, accessToken, userError, userData, initializedAccessToken])

  return (
    <>
      {initialized && (
        <LoginInfoContext.Provider value={loginInfo}>{children}</LoginInfoContext.Provider>
      )}
      {splashVisible && <Splash hide={initialized && splashMinElapsed} />}
    </>
  )
}

export const useLoginInfo = () => {
  return useContext(LoginInfoContext)
}
